import React, { useRef, useLayoutEffect, useState, useEffect, useContext } from 'react'
import { FormFieldLabel } from 'components/spreadsheet/components/form/fieldLabel'
import { IColumnTypes } from 'components/spreadsheet/types'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import { EDITABLE_DATETIME_OPTION_TYPES } from 'components/spreadsheet/constants/const'
import {
  toUTCString,
  formatDate,
  formatDateTime,
  safeFormatDateISOString,
  safeFormatDateTimeISOString
} from 'components/spreadsheet/helpers/format'
import { isDate } from 'helpers/date'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import DatePicker from 'components/date-picker'

const formatEditingValue = (value: string, kind: IColumnTypes): string => {
  if (kind === 'datetime') {
    return formatDateTime(value + 'Z', 'YYYY-MM-DDTHH:mm:ss')
  }
  return formatDate(value, 'YYYY-MM-DD')
}

interface DateCellProps {
  value: string | null
  align: 'center' | 'left' | 'right'
  color: string
  kind: IColumnTypes
  readOnly: boolean
  editing: boolean
  rowHeight: number
  selected: boolean
  validation: boolean
  handleKeyDown: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | null) => void
  dateFormat: string | null
  /** In a form: the date input shows all the time, as a field does, so a click goes into the part clicked. */
  asFormField?: boolean
  /** In a form: saves a day picked from the calendar straight away, as a click on an option does. */
  onPick?: (value: string | null) => void
}

const styles = cellStyles()

const DateCellComponent: React.FC<DateCellProps> = ({
  value,
  align,
  color,
  kind,
  readOnly,
  editing,
  rowHeight,
  selected,
  validation,
  handleKeyDown,
  setCellValue,
  dateFormat,
  asFormField,
  onPick
}) => {
  // In a form, named as the field it's drawn in.
  const labelledBy = useContext(FormFieldLabel)
  const dateRef = useRef<HTMLInputElement>(null)
  // In a form, whether a date has been typed since it was last saved.
  const typed = useRef(false)
  const heightPx = getRowHeightVariable(rowHeight)
  // A form's field shows what's typed or picked as it is, and follows the value when it changes from outside.
  const [draft, setDraft] = useState<string>(() => (value ? formatEditingValue(value, kind) : ''))
  useEffect(() => {
    if ((toUTCString(draft, kind) || null) !== (value || null)) setDraft(value ? formatEditingValue(value, kind) : '')
  }, [value])

  useLayoutEffect(() => {
    if (dateRef.current && editing) {
      dateRef.current.focus()
    }
  }, [editing])

  // In a form it's the same input whether or not the field is being edited, so the click that starts editing
  // lands in it rather than on text that's swapped for it. The day can be typed, or picked from the calendar at
  // the field's end, in place of the browser's own.
  if (asFormField && EDITABLE_DATETIME_OPTION_TYPES.includes(kind) && !readOnly) {
    return (
      <div data-date-field className="flex w-full items-center pr-1" data-focussed={selected || undefined}>
        <input
          aria-labelledby={labelledBy}
          className={styles.editing({
            focused: true,
            class: 'cell-value-editing min-w-0 flex-1 [&::-webkit-calendar-picker-indicator]:hidden'
          })}
          type={kind === 'datetime' ? 'datetime-local' : kind}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value)
            setCellValue(toUTCString(event.target.value, kind) || null)
            typed.current = true
          }}
          // A typed date is saved to the form once the field is left, as a picked day is straight away.
          onBlur={() => {
            if (!typed.current) return
            typed.current = false
            onPick?.(toUTCString(draft, kind) || null)
          }}
          onKeyDown={handleKeyDown}
          ref={dateRef}
          step={dateFormat?.includes('ss') ? 1 : undefined}
        ></input>
        <DatePicker
          labelledBy={labelledBy}
          value={draft.slice(0, 10)}
          onChange={(day) => {
            // A date and time keeps its time when its day is picked.
            const next = kind === 'datetime' ? `${day}T${draft.slice(11) || '00:00'}` : day
            setDraft(next)
            const picked = toUTCString(next, kind) || null
            setCellValue(picked)
            onPick?.(picked)
          }}
        />
      </div>
    )
  }

  if (EDITABLE_DATETIME_OPTION_TYPES.includes(kind) && editing && !readOnly) {
    return (
      <input
        aria-labelledby={labelledBy}
        className={styles.editing({ focused: true, class: 'cell-value-editing resize-none w-[inherit]' })}
        data-focussed
        type={kind === 'datetime' ? 'datetime-local' : kind}
        style={{ height: `${heightPx - 1}px` }}
        defaultValue={value ? formatEditingValue(value, kind) : ''}
        onChange={(event) => setCellValue(toUTCString(event.target.value, kind) || null)}
        onKeyDown={handleKeyDown}
        ref={dateRef}
        step={dateFormat?.includes('ss') ? 1 : undefined}
      ></input>
    )
  }

  const dateValue = (() => {
    if (!value || typeof value !== 'string' || !isDate(kind === 'date' ? value : value + 'Z')) return ''
    return kind === 'date'
      ? safeFormatDateISOString(value, dateFormat ? dateFormat : 'YYYY-MM-DD')
      : safeFormatDateTimeISOString(value, dateFormat ? dateFormat : 'YYYY-MM-DD HH:mm:ss')
  })()

  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value' })}
      data-focussed={selected || undefined}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)',
        maxHeight: `${heightPx}px`
      }}
      onDoubleClick={(event) => !EDITABLE_DATETIME_OPTION_TYPES.includes(kind) && event.stopPropagation()}
    >
      <div
        className={styles.content({ validation, class: 'truncate flex items-center' })}
        style={{
          maxHeight: `${heightPx - 3}px`
        }}
      >
        {dateValue || <span>&nbsp;</span>}
      </div>
    </div>
  )
}

export const DateCell = DateCellComponent
